import { modals, useModals } from '@mantine/modals';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchUser from 'src/Hooks/Queries/useFetchUser';

/**
 * **THE PROBLEM (A React Render Race Condition):**
 *
 * A simpler implementation would use a single `useEffect` with a local `isModalOpen` state.
 * However, this creates a render conflict:
 *
 * 1. The `useUpdateUser` mutation (inside the modal) calls `queryClient.setQueryData`
 * on success. This immediately updates the `user` data here, causing this
 * component (`RequireProfileCompletion`) to re-render.
 *
 * 2. In the *exact same "tick"*, the mutation's `onSuccess` callback also fires,
 * which closes the modal (`context.closeModal`).
 *
 * 3. The modal's `onClose` prop (which we *would* use to set `isModalOpen(false)`)
 * fires *during* the `ModalsProvider` re-render.
 *
 * 4. This `setIsModalOpen(false)` call conflicts with the re-render from `setQueryData`,
 * causing React's "Cannot update..." warning. This warning breaks the state update,
 * `isModalOpen` remains `true`, and the modal re-opens immediately.
 *
 * **THE SOLUTION (Decoupled Open/Close Logic):**
 *
 * We decouple the "open" and "close" logic into two separate `useEffect` hooks:
 *
 * 1. **Open `useEffect`:** This hook's only job is to **open** the modal.
 * - It checks if the modal *should* be open (user exists, profile incomplete, plan selected)
 * AND that it isn't *already* open (using `modalId` and `isOpeningModal.current`).
 * - If so, it opens the modal and stores its `id` in the `modalId` state.
 *
 * 2. **Close `useEffect`:** This hook's only job is to **close** the modal.
 * - It watches for `isProfileComplete` to become `true` (which it will when
 * `setQueryData` updates the `user` prop).
 * - If the profile is complete AND we have an open modal (`modalId` is set),
 * it programmatically closes it using `modals.closeModal(modalId)`.
 *
 * This pattern ensures that the modal's lifecycle is driven purely by the `user`
 * data prop, not by conflicting callbacks.
 */

const RequireProfileCompletion = ({ children }: { children: React.ReactNode }) => {
  const { data: user, isLoading } = useFetchUser({
    refetchOnWindowFocus: false,
  });
  const { t } = useTranslation();

  const [modalId, setModalId] = useState<string | null>(null);

  const isOpeningModal = useRef(false);

  // Don't stack on top of another context modal (e.g. the welcome video after
  // the first asset creation) — wait until it closes, the effect re-runs then.
  const { modals: openedModals } = useModals();
  const anotherModalOpened = openedModals.length > 0 && !modalId;

  const isProfileComplete =
    !!user?.lastName && !!user?.firstName && !!user?.phoneNumber && !!user?.title;

  useEffect(() => {
    const shouldOpen = user && !isProfileComplete && !anotherModalOpened;

    if (shouldOpen && !modalId && !isOpeningModal.current) {
      isOpeningModal.current = true;

      const id = modals.openContextModal({
        modal: 'profileCompletion',
        withCloseButton: false,
        title: t('modals.profileCompletion.title', 'Personnalisez votre profil avant de continuer'),
        centered: true,
        size: 'lg',
        innerProps: {},
        onClose: () => {
          setModalId(null);
          isOpeningModal.current = false;
        },
      });
      setModalId(id);
    }
  }, [user, isProfileComplete, anotherModalOpened, t, modalId]);

  useEffect(() => {
    if (modalId && isProfileComplete) {
      modals.close(modalId);
      setModalId(null);
      isOpeningModal.current = false;
    }
  }, [modalId, isProfileComplete]);

  if (isLoading) {
    return null;
  }

  return children;
};

export default RequireProfileCompletion;
